import {
  useAnalytics as _useAnalytics,
  AnalyticsProvider as AProvider,
} from 'use-analytics';
import {
  AnalyticsEventProperties,
  AnalyticsTarget,
  UnionOfEventsWithProperties,
} from './types';
import React, { useCallback } from 'react';
import type { AnalyticsInstance } from 'analytics';
type Nullable<T> = { [key in keyof T]: T[keyof T] | null };

type UTMParams = Nullable<{
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_term: string;
  utm_content: string;
}>;

const getUTMParams = (): UTMParams => {
  const urlParams = new URLSearchParams(window.location.search);
  return {
    utm_source: urlParams.get('utm_source'),
    utm_medium: urlParams.get('utm_medium'),
    utm_campaign: urlParams.get('utm_campaign'),
    utm_term: urlParams.get('utm_term'),
    utm_content: urlParams.get('utm_content'),
  };
};

export const initializeAnalytics = () => {
  if (!sessionStorage.getItem('initial_referrer')) {
    sessionStorage.setItem('initial_referrer', document.referrer);
    // sessionStorage.setItem('initial_landing_page', window.location.href);
    sessionStorage.setItem(
      'initial_utm_params',
      JSON.stringify(getUTMParams()),
    );
  }
};

export const getCommonProperties = () => ({
  initial_referrer:
    sessionStorage.getItem('initial_referrer') ?? document.referrer,
  // todo in future with more sepcs
  // initial_landing_page:
  //   sessionStorage.getItem('initial_landing_page') ?? window.location.href,
});

const getTrackingProperties = (additionalProps = {}, withCampaign = false) => {
  const utmParams = JSON.parse(
    sessionStorage.getItem('initial_utm_params') || '{}',
  );
  const commonProps = getCommonProperties();
  const campaign = withCampaign
    ? {
        name: utmParams.utm_campaign,
        source: utmParams.utm_source,
        medium: utmParams.utm_medium,
        term: utmParams.utm_term,
        content: utmParams.utm_content,
      }
    : {};
  return {
    ...commonProps,
    ...additionalProps,
    ...(Object.keys(campaign).length ? campaign : undefined),
  };
};

export const useAnalytics = <TEvents extends string, TEventsWithProps>(
  additionalProperties?: Record<string, unknown>,
) => {
  const analyticsContext = _useAnalytics();

  const { track, identify, page } = analyticsContext;

  const trackPageView = useCallback(() => {
    page(getTrackingProperties(additionalProperties));
  }, [page, additionalProperties]);

  const identifyUser = (userId: string, userTraits?: any) => {
    identify(userId, {
      ...getTrackingProperties(additionalProperties),
      ...userTraits,
    });
  };
  function trackEvent<T extends AnalyticsTarget<TEvents, TEventsWithProps>>(
    event: UnionOfEventsWithProperties<T['EventsWithProps']>,
    properties: UnionOfEventsWithProperties<T['EventsWithProps']>,
  ): void;
  function trackEvent<
    T extends AnalyticsTarget<TEvents, TEventsWithProps>,
    EventName extends Exclude<
      T['Events'],
      keyof UnionOfEventsWithProperties<T['EventsWithProps']>
    >,
  >(
    event: EventName,
    properties?: AnalyticsEventProperties<
      TEvents,
      TEventsWithProps,
      T,
      EventName
    >,
  ): void;
  function trackEvent<
    T extends AnalyticsTarget<TEvents, TEventsWithProps>,
    EventName extends T['Events'],
  >(
    event: EventName,
    properties: AnalyticsEventProperties<
      TEvents,
      TEventsWithProps,
      T,
      EventName
    > = {} as AnalyticsEventProperties<TEvents, TEventsWithProps, T, EventName>,
  ) {
    const props =
      properties && typeof properties === 'object' ? properties : {};

    track(event, { ...getTrackingProperties(additionalProperties), ...props });
  }
  const disablePluginsTracking = analyticsContext.plugins.disable;
  const enablePluginsTracking = analyticsContext.plugins.enable;

  const callbackTrackEvent = useCallback(trackEvent, [
    track,
    additionalProperties,
  ]);
  return {
    trackEvent: callbackTrackEvent,
    trackPageView,
    identifyUser,
    disablePluginsTracking,
    enablePluginsTracking,
  };
};

export const AnalyticsProvider: React.FC<{
  children: React.ReactNode;
  instance: AnalyticsInstance;
}> = ({ children, instance }) => {
  return <AProvider instance={instance}>{children}</AProvider>;
};
